import type { Badge } from './badge';
import type { LifecycleStatus } from './competition';
export type { Badge } from './badge';

/**
 * `member_manager` is the desk role: it opens the roster and the entry lists
 * and nothing else — no walls, no settings, no signing anything.
 *
 * A club's **team manager** is deliberately not a staff role: that one is named
 * by the circuit on one of its clubs, and holds nothing at a gym at all.
 */
export type StaffRole = 'admin' | 'editor' | 'judge' | 'routesetter' | 'member_manager';

/**
 * What kind of place a staff record is attached to. The gym-admin panel manages
 * gyms, crags, trophies and the stages of a trophy — each with its own sections.
 */
export type StaffLocationType = 'gym' | 'crag' | 'competition' | 'trophy' | 'location';

export interface UserStaff {
  /** The location's slug. Named `gym_*` because that is what the API sends. */
  gym_slug: string;
  gym_name: string;
  /** 128px logo, so the gym-admin sidebar needs no fetch of its own. */
  gym_logo_small_url?: string | null;
  /** Absent on payloads cached before crags were manageable — read as a gym. */
  location_type?: StaffLocationType;
  status: 'pending' | 'confirmed' | 'rejected';
  /**
   * Where the *place* is in its life — not the same thing as `status` above,
   * which is this person's invitation. Null for a gym or a crag, which have no
   * life cycle. The switcher hides archived places with it but keeps the row,
   * so their panel still opens by URL and a season can be taken back out of
   * the archive.
   */
  location_status?: LifecycleStatus | null;
  staff_roles: StaffRole[];
  /**
   * True when there is no staff record behind this: a stage the user reaches
   * only because they work at its trophy, or an event because they work at the
   * gym that hosts it. Nothing is gated on it — the roles are real — it exists
   * so the panel can say where the access came from.
   */
  inherited?: boolean;
  /**
   * The slug of the place this one hangs under *for this user*: a stage's
   * trophy, or a hosted event's gym. What lets the switcher file competitions
   * under the place they came through instead of loose among the gyms.
   */
  parent_slug?: string | null;
  /**
   * What a competition hangs off, whoever is looking. A stage (`trophy_slug`
   * set) keeps no categories of its own; one held at a gym (`venue_slug` set)
   * keeps no climbing area of its own. Both null on anything else.
   */
  trophy_slug?: string | null;
  venue_slug?: string | null;
}

/** A staff record's location type, defaulting to the gym every old payload is. */
export function staffLocationType(staff: UserStaff | null | undefined): StaffLocationType {
  return staff?.location_type ?? 'gym';
}

export interface Follower {
  username: string;
  slug: string;
  avatar_small_url?: string;
}

/**
 * Whether this is a platform admin — `User#admin?` on the server, the `role`
 * column. Not to be confused with the `admin` **staff** role, which only ever
 * means "runs this gym": a gym's own admin is not one of these.
 */
export function isSuperAdmin(user: Pick<User, 'role'> | null | undefined): boolean {
  return user?.role === 'admin';
}

export interface User {
  id: number;
  email: string;
  username: string;
  slug: string;
  name: string;
  surname: string;
  gender: string;
  language: string;
  role: string;
  full_name: string;
  language_name: string;
  avatar_url?: string;
  avatar_small_url?: string;
  avatar_medium_url?: string;
  /**
   * What to draw when there is no picture — the palette name, not a hex value
   * (see lib/avatar-colors). Null until somebody picks one.
   */
  avatar_color?: string | null;
  created_at: string;
  updated_at: string;
  staff?: UserStaff[];
  is_following?: boolean;
  /** Everybody, while `followers` is only a preview of the newest few. */
  followers_count?: number;
  followers?: Follower[];
  feature_flags?: {
    new_homepage?: boolean;
    early_adopter?: boolean;
    /** Granted by authorizing at a device; reveals the gym's boards as tabs. */
    boards?: boolean;
  };
  total_points?: number;
  points_by_category?: { climb: number; social: number };
  badges?: Badge[];
  /** Whether this user is a coach (allenatore) who can be invited to build plans. */
  coach?: boolean;
  /** Secret token backing the coach's shareable invite link. Owner-only. */
  coach_token?: string;
  /**
   * ISO `YYYY-MM-DD`, what entering a competition places the person by.
   * Owner-only, and null until they give it.
   */
  birth_date?: string | null;
  /**
   * The devices push still reaches this person on (touched in the last 60
   * days), on any browser. Owner-only; once it is above zero the app stops
   * asking them to turn push on.
   */
  push_devices_count?: number;
}

export interface ProfileResponse {
  user: User;
}

export interface ProfileUpdateRequest {
  user: {
    name: string;
    surname: string;
    username: string;
    gender: string;
    /** Null clears it. */
    birth_date?: string | null;
    email: string;
    language: string;
    avatar?: string;
    early_adopter?: boolean;
    coach?: boolean;
  };
}

export interface ProfileUpdateResponse {
  message: string;
  user: User;
  offline?: boolean; // Flag to indicate if response is from offline queue
}

export interface PasswordUpdateRequest {
  user: {
    current_password: string;
    password: string;
    password_confirmation: string;
  };
}

export interface PasswordUpdateResponse {
  message: string;
  offline?: boolean; // Flag to indicate if response is from offline queue
}
